<script>
import Form from '@classes/Form'
import ComboBox from '@components/combo-box.vue'
import { medicalDateFormatter, isBeforeDate } from '@utils/medical-date'
import { authComputed, authMethods, optionsComputed } from '@state/helpers'
import { calendarService } from '@services/CalendarService'
import { immunizationService } from '@services/ImmunizationService'
import { healthRecordService } from '@services/HealthRecordService'
import store from '@state/store'
import MedicalDatePickerDialog from '@components/dialogs/medical-date-picker-dialog.vue'
import hrMixin from '@src/mixins/hr-mixin.vue'
export default {
  page: {
    title: 'Immunization Editor',
    meta: [{ name: 'description', content: 'The Immunization Editor page.' }],
  },
  components: {
    ComboBox,
    MedicalDatePickerDialog,
  },
  mixins: [hrMixin],
  props: {
    formDialog: {
      type: Boolean,
      default: false,
    },
    formChanged: {
      type: Boolean,
      default: false,
    },
    formSaved: {
      type: Boolean,
      default: false,
    },
    formSavedAnother: {
      type: Boolean,
      default: false,
    },
    formDeleted: {
      type: Boolean,
      default: false,
    },
    attachmentDeleted: {
      type: Boolean,
      default: false,
    },
    create: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    id: {
      type: [Number, String],
      default: 0,
    },
    resource: {
      type: String,
      default: 'immunization',
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      vaccinationForm: new Form({
        immunization: '',
        date: null,
        date_part: '',
        dose_number: '',
        series_doses: '',
        vaccination_stop: false,
        vaccination_stop_reason: '',
        reaction: '',
        note: '',
        private: false,
      }),
      isMultipleDosages: '',
      vaccinationError: null,
      medicalDatePickerDialog: false,
      rules: {
        integerRule: (value) => {
          if (value) {
            if (typeof value === 'string') {
              if (!value.trim()) return true
            }
            if (!isNaN(parseInt(value)) && value >= 1 && value <= 100) {
              return true
            }
            return this.$t('health-record.vaccinations.integer-rule-message')
          }
          return true
        },
      },
      attachments: [],
      originalAttachments: [],
      showHRActions: true,
    }
  },
  computed: {
    ...authComputed,
    ...optionsComputed,
    reactions() {
      return this.getImmunizationOptions?.reaction
        ? this.getImmunizationOptions.reaction
        : []
    },
    canSaveImmunization() {
      if (
        (this.vaccinationForm.date ||
          this.vaccinationForm.date_part === 'past') &&
        this.vaccinationForm.immunization &&
        this.vaccinationError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
  },
  watch: {
    vaccinationForm: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
    isMultipleDosages(newVal, oldVal) {
      if (newVal === 'yes' && !this.edit) {
        this.vaccinationForm.dose_number = 1
        this.vaccinationForm.series_doses = 2
      }
      if (newVal === 'no') {
        this.vaccinationForm.dose_number = 1
        this.vaccinationForm.series_doses = 1
      }
    },
  },
  async mounted() {
    try {
      if (this.edit) {
        if (this.item.patient_id !== null) {
          this.showHRActions = false
        }
        this.itemToForm(this.resource, this.item)
      }

      if (!this.getImmunizationOptions) {
        const immunizationOptions = (await immunizationService.options()).data
        if (immunizationOptions) {
          store.dispatch('options/setImmunizationOptions', immunizationOptions)
        }
      }

      if (!this.getCalendarOptions) {
        const calendarOptions = (await calendarService.options()).data
        if (calendarOptions) {
          store.dispatch('options/setCalendarOptions', calendarOptions)
        }
      }
    } catch (error) {
      console.warn(error)
    }
  },
  methods: {
    ...authMethods,
    setDate(date) {
      this.vaccinationForm.stop_date = date
    },
    clearForm() {
      this.vaccinationForm.reset()
      this.message = false
      this.attachments = []
      this.isAnother = false
      this.isContinue = false
      this.vaccinationError = null
      this.dateError = null
    },
    setMedicalDate(medicalDate, past) {
      const field = this.medicalDatePickerName
      const fieldPart = this.medicalDatePickerName + '_part'

      const medicalDateObject = medicalDateFormatter(medicalDate)

      this.vaccinationForm[field] = medicalDateObject.date
      this.vaccinationForm[fieldPart] = this.determinePast(
        past,
        medicalDateObject
      )
      this.medicalDatePickerDialog = false
    },
    async getImmunizationResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.vaccinationForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await immunizationService.create(
            this.vaccinationForm.removeEmptyFromData()
          )
        ).data
      }
    },
    async deleteAttachment(id) {
      try {
        const response = await immunizationService.attachmentDelete(id)
        if (response) {
          this.originalAttachments = this.originalAttachments.filter(function (
            obj
          ) {
            return obj.id !== id
          })
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setErrors(error) {
      this.vaccinationForm.onFail(error.response.data)
      if (this.hasResponse) {
        this.message = true
        if (this.formDialog) {
          this.$emit('update:formSaved', true)
        }
      }
      this.vaccinationError = error
    },
    async tryToSave(action) {
      this.resetFlagsAndErrors('vaccinationError', action)
      try {
        if (
          this.vaccinationForm.date &&
          isBeforeDate(
            this.vaccinationForm.date,
            new Date().toISOString().split('T')[0]
          )
        ) {
          this.dateError = this.$t('start-after-today')
          this.scrollToErrors()
          return
        }

        const response = await this.getImmunizationResponse()

        if (response) {
          this.hasResponse = true
          let responseAttachments

          if (this.attachments.length > 0) {
            responseAttachments = (
              await immunizationService.attachments(
                this.attachments,
                response.hr_id,
                'immunization'
              )
            ).data
            if (responseAttachments)
              if (document.getElementById('getAllData'))
                console.warn(responseAttachments)
          }

          await this.fetchWizardData()

          this.message = true
          if (
            action === 'continue' &&
            store.state.calender.addNewRecord === 'true'
          ) {
            this.$emit('update:formSaved', true)
            this.$emit('close-dialog')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'continue') {
            this.$emit('update:formSaved', true)
            this.edit ? this.$emit('close-dialog') : this.$emit('back')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'another') {
            this.$emit('update:formSavedAnother', true)
            this.clearForm()
            this.$emit('add-another')
            if (document.getElementById('updateGetAllData'))
              document.getElementById('updateGetAllData').click()
          }
        }
      } catch (error) {
        console.warn(error)
        this.setErrors(error)
        this.isAnother = false
        this.isContinue = false
        this.scrollToErrors()
      } finally {
        this.tryingToSave = false
      }
    },
    itemToForm(res, item) {
      if (item.record.series_doses > 1) {
        this.isMultipleDosages = 'yes'
      }

      if (item.record.date) {
        this.vaccinationForm.date = item.record.date
      }
      this.vaccinationForm.date_part = item.record.date_part
      this.vaccinationForm.immunization = item.record.immunization
      this.vaccinationForm.series_doses = item.record.series_doses
      this.vaccinationForm.dose_number = item.record.dose_number
      this.vaccinationForm.reaction = item.record.reaction?.key
      this.vaccinationForm.vaccination_stop = item.record.vaccination_stop
      this.vaccinationForm.vaccination_stop_reason =
        item.record.vaccination_stop_reason
      this.vaccinationForm.note = item.note
      this.vaccinationForm.private = item.private
      this.originalAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
  },
}
</script>

<template>
  <v-container @dragover.prevent @drop="onDrop">
    <v-row
      no-gutters
      :class="$style.hrHeader"
      class="primary--text mt-1"
      justify="center"
    >
      <div class="mr-4">
        <v-img
          :src="
            require('@assets/images/safehaven/health-record-icons/immunizations.svg')
          "
          height="30"
          width="30"
          contain
        ></v-img>
      </div>
      {{ $t('health-record.vaccinations.form_title') }}
    </v-row>

    <v-row
      class="mt-10 offblack--text"
      :class="$style.hrQuestionFull"
      no-gutters
      justify="center"
    >
      What's the name of the vaccine?
    </v-row>
    <v-row class="mt-10" no-gutters justify="center">
      <div :class="$style.hrComboBoxContainer">
        <ComboBox
          :key="'immunization-combo-1'"
          :disabled="show"
          :combo-class="'immunization-combo-1'"
          :value.sync="vaccinationForm.immunization"
          placeholder="e.g. COVID-19 Vaccine"
          :error-messages="
            vaccinationForm.errors.has('immunization')
              ? 'Immunization: ' + vaccinationForm.errors.get('immunization')
              : ''
          "
          fetch-url="/api/v1/hr/choices/immunization/"
          filled
          rounded
          background-color="input"
          :class="$style.hrComboBox"
          @keydown="
            clearFormFromErrors(
              'immunization',
              'vaccinationForm',
              // eslint-disable-next-line vue/comma-dangle
              'vaccinationError'
            )
          "
        ></ComboBox>
      </div>
    </v-row>

    <v-row class="mt-7">
      <v-col cols="12" md="6">
        <v-row
          :class="$style.hrQuestion"
          class="my-auto offblack--text"
          justify="center"
          no-gutters
        >
          Does it involve multiple dosages?
        </v-row>
      </v-col>
      <v-col cols="12" md="6">
        <v-radio-group
          v-model="isMultipleDosages"
          :disabled="show"
          row
          class="my-auto"
          :class="$style.hrRadio"
        >
          <v-row class="my-auto" justify-md="start" justify="center">
            <v-radio label="No" value="no"></v-radio>
            <v-radio label="Yes" value="yes"></v-radio>
          </v-row>
        </v-radio-group>
      </v-col>
    </v-row>

    <div v-if="isMultipleDosages === 'yes'">
      <v-row
        :class="$style.hrQuestion"
        class="mt-10 offblack--text"
        justify="center"
        no-gutters
      >
        How many dosages?
      </v-row>
      <v-row
        class="mt-10 offblack--text"
        :class="$style.hrQuestionFull"
        no-gutters
        justify="center"
      >
        <v-icon
          :disabled="vaccinationForm.series_doses <= 2 || show"
          color="primary"
          class="mr-7"
          @click="vaccinationForm.series_doses--"
          >mdi-minus-circle-outline</v-icon
        >
        {{ vaccinationForm.series_doses }}
        <v-icon
          color="primary"
          class="ml-7"
          :disabled="show"
          @click="vaccinationForm.series_doses++"
          >mdi-plus-circle-outline</v-icon
        >
      </v-row>

      <v-row
        :class="$style.hrQuestion"
        class="mt-10 offblack--text"
        justify="center"
        no-gutters
      >
        What dose number was this?
      </v-row>
      <v-row
        class="mt-10 offblack--text"
        :class="$style.hrQuestionFull"
        no-gutters
        justify="center"
      >
        <v-icon
          :disabled="vaccinationForm.dose_number <= 1 || show"
          color="primary"
          class="mr-7"
          @click="vaccinationForm.dose_number--"
          >mdi-minus-circle-outline</v-icon
        >
        {{ vaccinationForm.dose_number }}
        <v-icon
          :disabled="
            vaccinationForm.dose_number === vaccinationForm.series_doses || show
          "
          color="primary"
          class="ml-7"
          @click="vaccinationForm.dose_number++"
          >mdi-plus-circle-outline</v-icon
        >
      </v-row>
    </div>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-10 offblack--text"
      no-gutters
      justify="center"
    >
      When did the immunization happen?
    </v-row>
    <v-row v-if="dateError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{ dateError }}</p>
      </v-col>
    </v-row>
    <v-row class="mt-10" no-gutters justify="center">
      <div
        :class="mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv"
      >
        <MedicalDatePickerDialog
          :key="'immunizationDate-1'"
          :disabled="show"
          :dialog.sync="medicalDatePickerDialog"
          :title="'health-record.vaccinations.occurrence'"
          :field="vaccinationForm.date"
          :part="vaccinationForm.date_part"
          filled
          background-color="input"
          :error-messages="
            vaccinationForm.errors.has('date')
              ? 'Surgery Date: ' + vaccinationForm.errors.get('date')
              : ''
          "
          @input="clearErrors('date', 'vaccinationForm', 'vaccinationError')"
          @setMedicalDate="setMedicalDate"
          @showMedicalDate="showMedicalDate('date')"
        />
      </div>
    </v-row>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-7 offblack--text"
      no-gutters
      justify="center"
    >
      Any reactions to the vaccine?
    </v-row>
    <v-row class="mt-10" no-gutters justify="center">
      <v-autocomplete
        v-model="vaccinationForm.reaction"
        :disabled="show"
        class="vaccinationReaction"
        attach=".vaccinationReaction"
        :items="reactions"
        item-text="value"
        item-value="key"
        placeholder="Reaction"
        filled
        rounded
        clearable
        background-color="input"
        :class="$style.hrAutoComplete"
      ></v-autocomplete>
    </v-row>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-7 offblack--text"
      no-gutters
      justify="center"
    >
      Any additional files or notes you'd like to include?
    </v-row>
    <v-row class="mt-10">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <SpeechToText
          :key="'immunization-2'"
          :disabled="show"
          :value.sync="vaccinationForm.note"
          auto-grow
          rounded
          :class="$style.hrSpeechToText"
          :placeholder="$t('health-record.vaccinations.note')"
        ></SpeechToText>
      </div>
    </v-row>
    <v-row class="mt-10">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <FileUpload
          :key="'immunization-3'"
          :disabled="show"
          icon
          :height="200"
          :multiple="true"
          :files.sync="attachments"
          :non-draggable="true"
        ></FileUpload>
      </div>
    </v-row>

    <v-row v-if="edit" class="mt-10">
      <div class="mx-auto" :class="$style.hrInputDiv">
        <AttachmentList
          :disabled="show"
          :attachments="originalAttachments"
          @delete-attachment="deleteAttachment"
        ></AttachmentList>
      </div>
    </v-row>

    <ForMyEyesOnly v-if="!show" :value.sync="vaccinationForm.private" />

    <div v-if="showHRActions">
      <HrActions
        :can-save="canSaveImmunization"
        :is-another="isAnother"
        :is-continue="isContinue"
        :trying-to-save="tryingToSave"
        :trying-to-delete="tryingToDelete"
        :edit="edit"
        @go-back="goBack"
        @try-to-save="tryToSave"
        @try-to-delete="prepareDeletion"
      />
    </div>
    <v-row v-if="vaccinationError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{ $t('health-record.vaccinations.error') }}</p>
        <div v-if="vaccinationForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in vaccinationForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="message" class="ml-1">
      <p class="success--text">{{ $t('health-record.surgeries.message') }}</p>
    </v-row>
    <BaseConfirmDialog
      v-if="confirmDialog"
      :dialog="confirmDialog"
      :message="computedMessage"
      title="Delete Item"
      @cancel="cancel"
      @confirm="deleteItem"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.hrHeader {
  // styleName: Desktop/Display X-Small Bold;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestion {
  // styleName: Desktop/Display X-Small;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionFull {
  // styleName: Desktop/Display X-Small;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrRadio {
  // styleName: Mobile / Display Small;
  font-family: $system-default-font-family;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrComboBoxContainer {
  width: 394px;
}

.hrComboBox {
  border-radius: 15px !important;
}
.hrTextField {
  max-width: 394px !important;
  background: $color-theme-light-input;
  border-radius: 15px !important;
}
.hrAutoComplete {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrSelect {
  max-width: 394px !important;
  background: $color-theme-light-input;
  border-radius: 15px !important;
}
.hrInputDiv {
  width: 394px;
  border-radius: 15px !important;
}
.hrSpeechToText {
  max-width: 394px !important;
  background: $color-theme-light-input;
}
.hrButton {
  border-radius: 8px !important;
}
.medicalDateDiv {
  width: 394px;
  margin-right: auto;
  margin-left: auto;
}
.medicalDateDivMobile {
  width: 60%;
  margin-right: auto;
  margin-left: auto;
}
</style>
